import {
  DeployEnvironment,
  DynamicImageSizeIds,
  DynamicImageUrl,
  DynamicImageWithStatus,
  DynamicImageActive,
  DynamicMediaImage,
  ImageStatus,
  ImageError,
  SelectionDetail
} from '@client/typings/Common';
import { getLocalizedString, WorldReadyProps } from './localeHelper';

export const getImageHostName = (targetEnv: DeployEnvironment | undefined): string => {
  if (targetEnv === DeployEnvironment.STAGING || targetEnv === DeployEnvironment.DEVELOPMENT) {
    return 'https://pics.msmaster.qa.paypal.com';
  }
  if (targetEnv === DeployEnvironment.SANDBOX) {
    return 'https://pics.sandbox.paypal.com';
  }
  return 'https://pics.paypal.com';
};

export const splitImageAssetIds = (assetIds: string): string[] => {
  if (!assetIds) {
    return [];
  }
  return assetIds.split(',');
};

export const joinImageAssetIds = (assetIdArr: string[]): string => {
  return assetIdArr.join(',');
};

export const transDynamicImageAssetIdToExampleImageUrl = (
  assetId: string,
  targetEnv?: DeployEnvironment
): string => {
  const hostName = getImageHostName(targetEnv);
  const imagePath = global.atob(assetId); // e.g. /00/p/OTJiNzU3MDAtNDQ3NS00M2QxLWJkYTItZDYyN2FiYzQ1OTVk/image_12.PNG
  return hostName + imagePath;
};

export const transDynamicImageAssetIdsToImagesWithStatus = (
  assetIds: string,
  targetEnv?: DeployEnvironment
): DynamicImageActive[] => {
  const assetIdArr = splitImageAssetIds(assetIds);
  return assetIdArr.map((assetId) => {
    return {
      status: ImageStatus.ACTIVE,
      assetId,
      exampleImageUrl: transDynamicImageAssetIdToExampleImageUrl(assetId, targetEnv)
    };
  });
};

export const transDynamicMediaImageToImageWithStatus = (
  mediaImage: DynamicMediaImage
): DynamicImageWithStatus => {
  return {
    status: ImageStatus.ACTIVE,
    assetId: mediaImage.assetId,
    exampleImageUrl:
      mediaImage.links.find((i) => i.sizeId === DynamicImageSizeIds.DIMENSION_500x500)?.href || '' // e.g. https://pics.msmaster.qa.paypal.com/00/p/NDNlZjJiOWUtYmU4OS00NmU5LTlmY2UtNTM2OTk1NWJhNDIx/image_12.PNG
  };
};

/**
 * Transform the dynamic image to another size by replacing size id in the url.
 * @param dynamicImageUrl One cdn url of the image. e.g. https://pics.msmaster.qa.paypal.com/00/p/NDNlZjJiOWUtYmU4OS00NmU5LTlmY2UtNTM2OTk1NWJhNDIx/image_12.PNG
 * @param sizeId size id in enum DynamicImageSizeIds
 * @returns another cdn url of the dynamic image
 */
export const transDynamicImageUrlBySizeId = (
  dynamicImageUrl: DynamicImageUrl,
  sizeId: DynamicImageSizeIds
): DynamicImageUrl => {
  return dynamicImageUrl.replace(/_[0-9]*/, `_${sizeId}`);
};

const ImageInlineErrorContentMapping = {
  [ImageError.TYPE_ERROR]: 'editor.image.uploader.error.file.type',
  [ImageError.SIZE_ERROR]: 'editor.image.uploader.error.file.size',
  [ImageError.SERVER_ERROR]: 'editor.image.uploader.error.service',
  [ImageError.DIMENSION_TOO_LOW_ERROR]: 'editor.image.uploader.error.low.dimension',
  [ImageError.DIMENSION_TOO_HIGH_ERROR]: 'editor.image.uploader.error.high.dimension'
};

export const getImageInlineErrorContent = (worldReady: WorldReadyProps, error: ImageError) => {
  return getLocalizedString(worldReady, ImageInlineErrorContentMapping[error]);
};

// Primarily being used for FPTI
export const getImageCount = (
  productImages?: string,
  selectionDetails?: SelectionDetail[]
): string => {
  const productImageAssetIds = splitImageAssetIds(productImages || '');
  const selectionDetailsWithImages =
    selectionDetails?.filter((selectionDetail) => selectionDetail.images) || [];
  return String(productImageAssetIds.length + selectionDetailsWithImages.length);
};
